<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Distribuicao por Dia',
  },
});

const maxCount = computed(() => {
  if (!props.data || props.data.length === 0) return 1;
  return Math.max(...props.data.map(d => d.count), 1);
});

const total = computed(() => {
  if (!props.data) return 0;
  return props.data.reduce((sum, d) => sum + d.count, 0);
});

function getBarHeight(count) {
  return `${(count / maxCount.value) * 100}%`;
}

function getBarColor(day) {
  // Weekend vs weekday colors
  if (day === 0 || day === 6) return 'bg-n-amber-9';
  return 'bg-n-blue-9';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-calendar-days text-lg text-n-blue-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-3 text-xs">
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-blue-9" />
          <span class="text-n-slate-11">Dia util</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-amber-9" />
          <span class="text-n-slate-11">Fim de semana</span>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!data || data.length === 0 || total === 0"
      class="flex items-center justify-center h-48 text-n-slate-11"
    >
      <span class="text-sm">Sem dados disponiveis</span>
    </div>

    <!-- Chart -->
    <div v-else class="flex flex-col">
      <div class="flex items-end justify-between gap-2 h-40 px-2">
        <div
          v-for="day in data"
          :key="day.day"
          class="flex-1 flex flex-col items-center gap-1"
        >
          <span class="text-xs font-medium text-n-slate-12">{{
            day.count
          }}</span>
          <div
            class="w-full bg-n-alpha-1 rounded-t-md relative h-full flex items-end"
          >
            <div
              class="w-full rounded-t-md transition-all duration-300"
              :class="getBarColor(day.day)"
              :style="{ height: getBarHeight(day.count) }"
            />
          </div>
        </div>
      </div>

      <!-- Labels -->
      <div
        class="flex justify-between gap-2 px-2 mt-2 pt-2 border-t border-n-weak"
      >
        <div
          v-for="day in data"
          :key="`label-${day.day}`"
          class="flex-1 text-center"
        >
          <span
            class="text-xs font-medium"
            :class="
              day.day === 0 || day.day === 6
                ? 'text-n-amber-11'
                : 'text-n-slate-11'
            "
          >
            {{ day.label.substring(0, 3) }}
          </span>
        </div>
      </div>

      <!-- Summary -->
      <div
        class="flex items-center justify-between mt-4 pt-3 border-t border-n-weak text-sm"
      >
        <span class="text-n-slate-11">Total no periodo</span>
        <span class="font-medium text-n-slate-12">{{ total }} conversas</span>
      </div>
    </div>
  </div>
</template>
